import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import withAncestry from 'ecommerce/hoc/withAncestry';
import useEditorItemContext from '../../../context/editorItemContext';
import DialogPanelBody from '../../components/dialogPanelBody';
import AddPageButton from '../../components/addPageButton';
import useAncestryFamilyTreeContext from '../../../context/ancestryFamilyTreeContext';
import SelectedPersonsCounter from '../../components/selectedPersonsCounter';
import PhotoSelectPanel from './photoSelectPanel';
import personGroups from '../../people/personGroups';
import AllPhotosPanel from './allPhotosPanel';
import objectFromEntries from 'utils/objectFromEntries';
import PersonGroupTabs from '../../people/personGroupTabs';
import PersonMultiSelectTabPanel from '../../people/personMultiSelectTabPanel';
import useModalContext from 'components/context/modal';
import useEditorContext from '../../../context/editorContext';
import { generateAncestryPhotosData } from '../../../generator/generateAncestryData';

const PhotoPanel = ({ onAddPages, openPanel }) => {
  const modalContext = useModalContext();
  const editorContext = useEditorContext();

  const { item, template } = useEditorItemContext();
  const { projectPersons } = useAncestryFamilyTreeContext();

  const [selectedPersons, setSelectedPersons] = useState([]);

  const displayedPersonGroups = useMemo(
    () => {
      const arr = (item.additionalInfo.treeId ? ['all', 'in-book'] : ['in-book']).map(id => personGroups[id]);

      arr.unshift({
        id: 'photos',
        title: 'All Photos',
        TabPanel: ({ panel }) => panel,
      });

      return arr
    },
    [item.additionalInfo.treeId],
  );

  const [selectedPeopleByGroup, setSelectedPeopleByGroup] = useState(
    objectFromEntries(displayedPersonGroups.map(({ id }) => [id, []])),
  );
  const [selectedPersonGroup, setSelectedPersonGroup] = useState(displayedPersonGroups[0]);

  const handlePersonGroupChange = useCallback(
    newSelectedPersonGroup => {
      const { id } = newSelectedPersonGroup;
      setSelectedPersonGroup(newSelectedPersonGroup);
      setSelectedPersons(selectedPeopleByGroup[id]);
    },
    [setSelectedPersons, selectedPeopleByGroup],
  );

  const handleRemove = useCallback(
    ({ person }) => {
      const { id } = selectedPersonGroup;
      setSelectedPeopleByGroup(currentSelectedPeopleByGroup => {
        const newSelectedPeople = currentSelectedPeopleByGroup[id].filter(
          selectedPerson => selectedPerson.id !== person.id,
        );
        setSelectedPersons(newSelectedPeople);
        return {
          ...currentSelectedPeopleByGroup,
          [id]: newSelectedPeople,
        };
      });
    },
    [setSelectedPersons, selectedPersonGroup],
  );

  const handleSelect = useCallback(
    ({ person }) => {
      const { id } = selectedPersonGroup;
      setSelectedPeopleByGroup(currentSelectedPeopleByGroup => {
        const newSelectedPeople = [...currentSelectedPeopleByGroup[id], person];
        setSelectedPersons(newSelectedPeople);
        return {
          ...currentSelectedPeopleByGroup,
          [id]: newSelectedPeople,
        };
      });
    },
    [setSelectedPersons, selectedPersonGroup],
  );

  const [selectedPhotos, setSelectedPhotos] = useState({});
  const selectedPhotoKeys = useMemo(() => {
    return Object.keys(selectedPhotos)
  }, [selectedPhotos])

  const handleAddPhotoPages = async () => {

    const loadingModalKey = modalContext.spinner(<>Assembling your creation ...</>, {
      title: 'Building your project',
    });

    const ancestryPhotos = Object.values(selectedPhotos).filter(photo => photo);

    console.log(ancestryPhotos)

    const { data, error } = await generateAncestryPhotosData(
      editorContext.accessToken,
      template,
      ancestryPhotos,
    );

    if (error) {
      modalContext.close(loadingModalKey);
      modalContext.confirm(<>{error}</>, {
        title: 'Error creating your project',
        closeable: false,
        confirmLabel: 'OK',
      });
      return;
    }

    editorContext.addPhotos(item.id, data.photos);
    // TODO filter pages
    const pages = data.pages.filter(page => true);

    modalContext.close(loadingModalKey);

    if (pages.length > 0) {
      onAddPages(pages, { sortByTags: true });
    } else {
      modalContext.confirm(<>Sorry, we didn&apos;t find any photos.</>, {
        title: 'No pages added',
        closeable: false,
        confirmLabel: 'OK',
      });
    }
  };

  const handleChoosePhotos = async () => {
    openPanel({
      props: { onAddPages, selectedPersons },
      renderPanel: PhotoSelectPanel,
      title: 'Choose Photo(s)',
    });
  };

  return (
    <DialogPanelBody
      actionButton={
        selectedPersonGroup.id === "photos" ?
          <>
            <SelectedPersonsCounter label="photo" selectedPersons={selectedPhotoKeys} />
            <AddPageButton
              icon={undefined}
              label="Add Photo Page(s)"
              disabled={selectedPhotoKeys.length === 0}
              onClick={handleAddPhotoPages}
            />
          </> :
          <>
            <SelectedPersonsCounter selectedPersons={selectedPersons} />
            <AddPageButton
              icon={undefined}
              label="Choose Photo(s)"
              disabled={selectedPersons.length === 0}
              onClick={handleChoosePhotos}
            />
          </>
      }
    >
      <PersonGroupTabs
        isLoading={projectPersons == null}
        onChange={handlePersonGroupChange}
        panel={
          selectedPersonGroup.id === "photos" ?
            <AllPhotosPanel selectedPhotos={selectedPhotos} onChange={setSelectedPhotos} /> :
            <PersonMultiSelectTabPanel
              onRemove={handleRemove}
              onSelect={handleSelect}
              selectedPeopleByGroup={selectedPeopleByGroup}
            />
        }
        people={projectPersons}
        personGroups={displayedPersonGroups}
        selectedPersonGroup={selectedPersonGroup}
        treeId={item.additionalInfo.treeId}
      />
    </DialogPanelBody>
  );
};

PhotoPanel.propTypes = {
  openPanel: PropTypes.func.isRequired,
  onAddPages: PropTypes.func.isRequired,
};

export default withAncestry(PhotoPanel);
